'use client';

/**
 * AdminQuickEditDialog
 *
 * Universal inline WYSIWYG edit dialog for super-admins.
 * Accepts any entity type + its data, renders the right fields,
 * and saves via the appropriate admin service function.
 *
 * Supported types: Artist | Track | Release | Product | Service | Event | News | Radio | Label | Chart
 */

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { Loader2, Upload, X, Pencil, Eye, EyeOff, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { storage } from '@/lib/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  updateMedia, updateRelease, updateEvent, updateMarketplaceItem, updateNewsArticle,
} from '@/services/admin';
import { updateArtist } from '@/services/artists';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

/* ── Field schema per entity type ───────────────────────────────────────── */
type FieldType = 'text' | 'textarea' | 'image' | 'tags' | 'select' | 'toggle' | 'date' | 'number';

interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
  placeholder?: string;
}

const SCHEMAS: Record<string, FieldDef[]> = {
  Artist: [
    { key: 'name',     label: 'Name',       type: 'text' },
    { key: 'bio',      label: 'Bio',        type: 'textarea' },
    { key: 'genres',   label: 'Genres',     type: 'tags',     placeholder: 'Add genre…' },
    { key: 'imageUrl', label: 'Profile Image', type: 'image' },
    { key: 'status',   label: 'Status',     type: 'select', options: [
      { value: 'active', label: 'Active' },
      { value: 'suspended', label: 'Suspended' },
      { value: 'pending', label: 'Pending' },
    ]},
  ],
  Label: [
    { key: 'name',        label: 'Name',       type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'imageUrl',    label: 'Image',       type: 'image' },
  ],
  Track: [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'artistName',  label: 'Artist',      type: 'text' },
    { key: 'genre',       label: 'Genre',       type: 'text' },
    { key: 'coverArtUrl', label: 'Cover Art',   type: 'image' },
    { key: 'status',      label: 'Status',      type: 'select', options: [
      { value: 'Released', label: 'Released' },
      { value: 'Draft', label: 'Draft' },
      { value: 'Unreleased', label: 'Unreleased' },
    ]},
    { key: 'adminHidden', label: 'Hidden from public', type: 'toggle' },
  ],
  Release: [
    { key: 'title',       label: 'Title',       type: 'text' },
    // Release docs store their type in `releaseType` (lowercase values) — this
    // used to write a `type` field that nothing reads, so the picker silently
    // did nothing.
    { key: 'releaseType', label: 'Release Type', type: 'select', options: [
      { value: 'album', label: 'Album' },
      { value: 'ep', label: 'EP' },
      { value: 'single', label: 'Single' },
      { value: 'mixtape', label: 'Mixtape' },
      { value: 'compilation', label: 'Compilation' },
      { value: 'live album', label: 'Live Album' },
      { value: 'demo', label: 'Demo' },
      { value: 'demo compilation', label: 'Demo Compilation' },
    ]},
    { key: 'artistName',  label: 'Artist',      type: 'text' },
    { key: 'coverArtUrl', label: 'Cover Art',   type: 'image' },
    { key: 'adminHidden', label: 'Hidden from public', type: 'toggle' },
  ],
  'Community Pick': [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'imageUrl',    label: 'Image',       type: 'image' },
  ],
  Product: [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'price',       label: 'Price (USD)', type: 'number' },
    { key: 'imageUrl',    label: 'Image',       type: 'image' },
    { key: 'adminHidden', label: 'Hidden from public', type: 'toggle' },
  ],
  Service: [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'price',       label: 'Price (USD)', type: 'number' },
    { key: 'imageUrl',    label: 'Image',       type: 'image' },
    { key: 'adminHidden', label: 'Hidden from public', type: 'toggle' },
  ],
  Event: [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'date',        label: 'Date/Time',   type: 'text',  placeholder: 'YYYY-MM-DD HH:mm' },
    { key: 'location',    label: 'Location',    type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'imageUrl',    label: 'Image',       type: 'image' },
    { key: 'category',    label: 'Category',    type: 'select', options: [
      { value: 'Music', label: 'Music' },
      { value: 'Party', label: 'Party' },
      { value: 'Festival', label: 'Festival' },
      { value: 'Workshop', label: 'Workshop' },
      { value: 'Conference', label: 'Conference' },
      { value: 'Club Night', label: 'Club Night' },
      { value: 'Other', label: 'Other' },
    ]},
    { key: 'status',      label: 'Status',      type: 'select', options: [
      { value: 'Upcoming', label: 'Upcoming' },
      { value: 'Ongoing', label: 'Ongoing' },
      { value: 'Cancelled', label: 'Cancelled' },
      { value: 'Postponed', label: 'Postponed' },
      { value: 'Completed', label: 'Completed' },
    ]},
  ],
  News: [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'excerpt',     label: 'Excerpt',     type: 'textarea' },
    { key: 'imageUrl',    label: 'Image',       type: 'image' },
    { key: 'status',      label: 'Status',      type: 'select', options: [
      { value: 'published', label: 'Published' },
      { value: 'draft', label: 'Draft' },
    ]},
    { key: 'adminHidden', label: 'Hidden from public', type: 'toggle' },
  ],
  Radio: [
    { key: 'name',        label: 'Station Name', type: 'text' },
    { key: 'genre',       label: 'Genre',        type: 'text' },
    { key: 'description', label: 'Description',  type: 'textarea' },
    { key: 'imageUrl',    label: 'Image',        type: 'image' },
    { key: 'isOn',        label: 'On Air',       type: 'toggle' },
  ],
  Chart: [
    { key: 'title',       label: 'Title',  type: 'text' },
    { key: 'artist',      label: 'Artist', type: 'text' },
    { key: 'genre',       label: 'Genre',  type: 'text' },
    { key: 'imageUrl',    label: 'Image',  type: 'image' },
  ],
  Playlist: [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'imageUrl',    label: 'Cover Image', type: 'image' },
  ],
  Video: [
    { key: 'title',       label: 'Title',  type: 'text' },
    { key: 'artist',      label: 'Artist', type: 'text' },
    { key: 'imageUrl',    label: 'Thumbnail', type: 'image' },
  ],
  User: [
    { key: 'displayName', label: 'Display Name', type: 'text' },
    { key: 'bio',         label: 'Bio',           type: 'textarea' },
  ],
};

/* ── Save dispatcher ─────────────────────────────────────────────────────── */
async function saveEntity(
  entityType: string,
  entityId: string,
  data: Record<string, unknown>,
): Promise<boolean> {
  try {
    switch (entityType) {
      case 'Artist': case 'Label':
        await updateArtist(entityId, data as any);
        return true;
      case 'Track':
        return await updateMedia(entityId, data as any);
      case 'Release':
        return await updateRelease(entityId, data as any);
      case 'Product': case 'Service':
        return await updateMarketplaceItem(entityId, data as any);
      case 'Event':
        return await updateEvent(entityId, data as any);
      case 'News':
        return await updateNewsArticle(entityId, data as any);
      case 'Radio':
        await updateDoc(doc(db, 'radioStations', entityId), data as any);
        return true;
      case 'Chart':
        await updateDoc(doc(db, 'charts', entityId), data as any);
        return true;
      case 'Playlist':
        await updateDoc(doc(db, 'playlists', entityId), data as any);
        return true;
      case 'Video':
        await updateDoc(doc(db, 'videos', entityId), data as any);
        return true;
      case 'Community Pick':
        await updateDoc(doc(db, 'communityPicks', entityId), data as any);
        return true;
      default:
        // Generic Firestore update — collection inferred from type
        await updateDoc(doc(db, entityType.toLowerCase() + 's', entityId), data as any);
        return true;
    }
  } catch (err) {
    console.error(`[AdminQuickEdit] save ${entityType}/${entityId}:`, err);
    return false;
  }
}

/* ── Storage folder per type ─────────────────────────────────────────────── */
function storageFolder(entityType: string, entityId: string): string {
  const map: Record<string, string> = {
    Artist: `artists/${entityId}`,
    Label: `labels/${entityId}`,
    Track: `media/${entityId}`,
    Release: `releases/${entityId}`,
    Product: `marketplace/${entityId}`,
    Service: `marketplace/${entityId}`,
    Event: `events/${entityId}`,
    News: `news/${entityId}`,
    Radio: `radio/${entityId}`,
    Chart: `charts/${entityId}`,
    Playlist: `playlists/${entityId}`,
    Video: `videos/${entityId}`,
  };
  return map[entityType] || `uploads/${entityId}`;
}

/* ── Image upload button ─────────────────────────────────────────────────── */
function ImageField({
  fieldKey, label, value, entityType, entityId,
  onChange,
}: {
  fieldKey: string; label: string; value: string;
  entityType: string; entityId: string;
  onChange: (val: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${storageFolder(entityType, entityId)}/${fieldKey}-${Date.now()}.${ext}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      onChange(url);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 items-start">
        {value && (
          <div className="relative h-14 w-14 rounded-md overflow-hidden flex-shrink-0 border border-border/40">
            <Image src={value} alt={label} fill className="object-cover" />
            <button
              onClick={() => onChange('')}
              className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </div>
        )}
        <div className="flex-1 space-y-1">
          <Input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Image URL"
            className="text-xs h-8"
          />
          <Button
            variant="outline" size="sm"
            className="w-full h-7 text-xs gap-1"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Upload
          </Button>
          <input
            ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Tag / chips field ────────────────────────────────────────────────────── */
function TagsField({
  label, value, onChange, placeholder,
}: {
  label: string; value: string[]; onChange: (v: string[]) => void; placeholder?: string;
}) {
  const [input, setInput] = useState('');

  const add = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !value.includes(trimmed)) onChange([...value, trimmed]);
    setInput('');
  };

  const remove = (tag: string) => onChange(value.filter(t => t !== tag));

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1 mb-1.5">
        {value.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 text-xs bg-primary/15 text-primary px-2 py-0.5 rounded-md border border-primary/20">
            {tag}
            <button onClick={() => remove(tag)} className="hover:text-destructive"><X className="h-2.5 w-2.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-1">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(input); } }}
          placeholder={placeholder || 'Add…'}
          className="text-xs h-8 flex-1"
        />
        <Button variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => add(input)}>+</Button>
      </div>
    </div>
  );
}

/* ── Props ───────────────────────────────────────────────────────────────── */
export interface AdminQuickEditProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  entityType: string;
  entityId: string;
  entityData: Record<string, unknown>;
  onSaved?: (updated: Record<string, unknown>) => void;
}

/* ── Main component ──────────────────────────────────────────────────────── */
export function AdminQuickEditDialog({
  open, onOpenChange, entityType, entityId, entityData, onSaved,
}: AdminQuickEditProps) {
  const fields = SCHEMAS[entityType] ?? SCHEMAS['Track'];
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  // Build initial form state from entityData + schema keys
  const buildInitial = () => {
    const init: Record<string, unknown> = {};
    fields.forEach(f => {
      init[f.key] = entityData[f.key] ?? entityData[f.key.replace('artistName', 'artist')] ?? (f.type === 'toggle' ? false : f.type === 'tags' ? [] : '');
      // Normalise nested artist.name → artistName
      if (f.key === 'artistName' && typeof entityData.artist === 'object' && entityData.artist) {
        init['artistName'] = (entityData.artist as any).name ?? '';
      }
    });
    return init;
  };

  const [form, setForm] = useState<Record<string, unknown>>(buildInitial);

  // Re-seed when entity changes
  useEffect(() => {
    setForm(buildInitial());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType]);

  const set = (key: string, val: unknown) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build save payload — only fields that appear in schema
      const payload: Record<string, unknown> = { ...form };
      const ok = await saveEntity(entityType, entityId, payload);
      if (ok) {
        toast({ title: `${entityType} updated` });
        onSaved?.(payload);
        onOpenChange(false);
      } else {
        toast({ title: 'Save failed', variant: 'destructive' });
      }
    } finally {
      setSaving(false);
    }
  };

  const title = (entityData.name || entityData.title || entityData.displayName || entityType) as string;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto gap-3 p-5">
        <DialogHeader className="pb-1">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Pencil className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-xs font-normal text-muted-foreground mr-1">{entityType}</span>
            <span className="truncate">{title}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {fields.map(f => {
            const val = form[f.key];

            if (f.type === 'image') {
              return (
                <ImageField
                  key={f.key}
                  fieldKey={f.key}
                  label={f.label}
                  value={(val as string) || ''}
                  entityType={entityType}
                  entityId={entityId}
                  onChange={v => set(f.key, v)}
                />
              );
            }

            if (f.type === 'tags') {
              return (
                <TagsField
                  key={f.key}
                  label={f.label}
                  value={Array.isArray(val) ? (val as string[]) : []}
                  onChange={v => set(f.key, v)}
                  placeholder={f.placeholder}
                />
              );
            }

            if (f.type === 'toggle') {
              return (
                <div key={f.key} className="flex items-center justify-between">
                  <Label className="text-xs cursor-pointer">{f.label}</Label>
                  <Switch
                    checked={Boolean(val)}
                    onCheckedChange={v => set(f.key, v)}
                    className="scale-90"
                  />
                </div>
              );
            }

            if (f.type === 'select' && f.options) {
              return (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Select value={(val as string) || ''} onValueChange={v => set(f.key, v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {f.options.map(o => (
                        <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }

            if (f.type === 'textarea') {
              return (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Textarea
                    value={(val as string) || ''}
                    onChange={e => set(f.key, e.target.value)}
                    rows={3}
                    className="text-xs resize-none"
                    placeholder={f.placeholder}
                  />
                </div>
              );
            }

            if (f.type === 'number') {
              return (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Input
                    type="number"
                    value={(val as string) || ''}
                    onChange={e => set(f.key, e.target.value)}
                    className="h-8 text-xs"
                    placeholder={f.placeholder}
                  />
                </div>
              );
            }

            // Default: text
            return (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                <Input
                  value={(val as string) || ''}
                  onChange={e => set(f.key, e.target.value)}
                  className="h-8 text-xs"
                  placeholder={f.placeholder}
                />
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 pt-2 border-t border-border/40">
          <Button
            className="flex-1 gap-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="text-sm">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Lightweight overlay wrapper ─────────────────────────────────────────── */
/**
 * Wrap any card with this to get an admin edit pencil.
 * Usage:
 *   <AdminEditOverlay entityType="Artist" entityId={artist.id} entityData={artist}>
 *     <YourCard />
 *   </AdminEditOverlay>
 */
export function AdminEditOverlay({
  entityType, entityId, entityData,
  onSaved, children, className,
}: {
  entityType: string; entityId: string; entityData: Record<string, unknown>;
  onSaved?: (updated: Record<string, unknown>) => void;
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('relative group/edit', className)}>
      {children}
      {/* Pencil button — appears on hover */}
      <button
        onClick={e => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        className={cn(
          'absolute top-1.5 right-1.5 z-30',
          'h-7 w-7 flex items-center justify-center',
          'bg-black/60 hover:bg-primary text-white rounded-[var(--btn-radius)]',
          'opacity-0 group-hover/edit:opacity-100 transition-opacity duration-150',
          'shadow-md',
        )}
        title={`Edit ${entityType}`}
        aria-label={`Edit ${entityType}`}
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <AdminQuickEditDialog
        open={open}
        onOpenChange={setOpen}
        entityType={entityType}
        entityId={entityId}
        entityData={entityData}
        onSaved={onSaved}
      />
    </div>
  );
}
